import ExcelJS from 'exceljs'
import FileSaver from 'file-saver'
import dayjs from 'dayjs'
import { assembleAssetTitle } from 'ComponentLibrary'
import { get, isEmpty } from 'lodash'
import { AssetWithValuations, MissingDataCellMeta } from 'ClientPortal/src/pages/Assets/AssetList/ExportMissingData/types'
import {
    checkIfEmpty,
    MissingDataExportCopy,
    STARTING_COLUMN_INDEX,
    STARTING_ROW_INDEX,
} from 'ClientPortal/src/pages/Assets/AssetList/ExportMissingData/constants'

function addSectionToWorksheet<T extends object>({
    worksheet,
    startRow,
    title,
    cells,
    rows,
    emptyMessage,
}: {
    worksheet: ExcelJS.Worksheet
    startRow: number
    title: string
    cells: MissingDataCellMeta<T>[]
    rows: T[]
    emptyMessage: string
}): number {
    let currentRow = startRow
    const titleRow = worksheet.getRow(currentRow)
    const titleCell = titleRow.getCell(STARTING_COLUMN_INDEX)
    titleCell.value = title
    titleCell.font = { size: 20 }
    currentRow++

    if (isEmpty(rows)) {
        const msgRow = worksheet.getRow(currentRow)
        const msgCell = msgRow.getCell(STARTING_COLUMN_INDEX)
        msgCell.value = emptyMessage
        msgCell.font = { size: 12, bold: true }
        msgCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF4C7C3' },
        }
        return ++currentRow
    }

    const headerRow = worksheet.getRow(currentRow)
    cells.forEach(({ header }, index) => {
        const cell = headerRow.getCell(index + STARTING_COLUMN_INDEX)
        cell.value = header
        cell.font = { size: 16 }
        cell.alignment = { horizontal: 'center' }
    })
    currentRow++

    for (const item of rows) {
        const row = worksheet.getRow(currentRow)
        cells.forEach(({ paths, format, greyedOut, zeroIsEmpty }, index) => {
            const cell = row.getCell(index + STARTING_COLUMN_INDEX)
            const firstValue = get(item, paths[0])
            const hasSomeValue = paths.some((path) => !checkIfEmpty(get(item, path), zeroIsEmpty))
            cell.value = format && firstValue ? format(firstValue) : hasSomeValue ? '✔' : greyedOut ? '✱' : '✘'
            cell.font = { size: 12 }
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: hasSomeValue ? 'FFB6E7B0' : greyedOut ? 'D3D3D3D3' : 'FFF4C7C3' },
            }
            cell.alignment = { horizontal: 'center' }
        })
        currentRow++
    }

    return currentRow
}

function prependSymbol(symbol: string, text: string) {
    return `${symbol} ${text}`
}

const getFileNameDateFormat = (locale: string) => (locale === 'en-US' ? 'MM-DD-YYYY' : 'DD-MM-YYYY')

// Adds a legend describing colors and symbols at the bottom of the worksheet
function addLegendToWorksheet({
    worksheet,
    startRow,
    copy,
}: {
    worksheet: ExcelJS.Worksheet
    startRow: number
    copy: MissingDataExportCopy
}): number {
    let currentRow = startRow

    const titleRow = worksheet.getRow(currentRow)
    const titleCell = titleRow.getCell(STARTING_COLUMN_INDEX)
    titleCell.value = copy.legendTitle
    titleCell.font = { size: 20 }
    currentRow++

    const legendItems: Array<{ color: string; symbol: string; meaning: string }> = [
        { color: 'FFB6E7B0', symbol: '✔', meaning: copy.legendMeaningAvailable },
        { color: 'FFF4C7C3', symbol: '✘', meaning: copy.legendMeaningMissing },
        { color: 'D3D3D3D3', symbol: '✱', meaning: copy.legendMeaningMissingOptional },
    ]

    for (const item of legendItems) {
        const row = worksheet.getRow(currentRow)
        const sampleCell = row.getCell(STARTING_COLUMN_INDEX)
        sampleCell.value = `${item.symbol} ${item.meaning}`
        sampleCell.alignment = { horizontal: 'left' }
        sampleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: item.color } }
        currentRow++
    }

    return currentRow
}

function transformSingleAssetMissingAssetDataToWorksheet({
    workbook,
    assetWithValuations,
    copy,
}: {
    workbook: ExcelJS.Workbook
    assetWithValuations: AssetWithValuations
    copy: MissingDataExportCopy
}) {
    const { asset, valuations } = assetWithValuations
    const structures = asset.economic_units.flatMap((eu) => eu.structures)
    const assetTitle = assembleAssetTitle({
        customer_asset_reference: asset.customer_asset_reference,
        ...asset.address,
    })

    let worksheetTitle = assetTitle.length > 31 ? `${assetTitle.substring(0, 28)}...` : assetTitle
    worksheetTitle = worksheetTitle.replace(/[[\]*/\\?:]/g, '')
    while (workbook.getWorksheet(worksheetTitle)) {
        worksheetTitle = `_${worksheetTitle}`
    }
    const worksheet = workbook.addWorksheet(worksheetTitle)
    let currentRow = STARTING_ROW_INDEX

    for (let col = STARTING_COLUMN_INDEX; col < copy.valuationCells.length + STARTING_COLUMN_INDEX; col++) {
        const column = worksheet.getColumn(col)
        column.width = 27
    }

    currentRow = addSectionToWorksheet({
        worksheet,
        startRow: currentRow,
        title: copy.sectionTitleAsset,
        cells: copy.assetCells,
        rows: [asset],
        emptyMessage: prependSymbol('✘', copy.emptyStateNoAsset),
    })
    currentRow++

    currentRow = addSectionToWorksheet({
        worksheet,
        startRow: currentRow,
        title: copy.sectionTitleStructures,
        cells: copy.structureCells,
        rows: structures || [],
        emptyMessage: prependSymbol('✘', copy.emptyStateNoStructures),
    })
    currentRow++

    currentRow = addSectionToWorksheet({
        worksheet,
        startRow: currentRow,
        title: copy.sectionTitleValuations,
        cells: copy.valuationCells,
        rows: valuations,
        emptyMessage: prependSymbol('✘', copy.emptyStateNoValuations),
    })

    currentRow++
    currentRow++
    currentRow++
    addLegendToWorksheet({ worksheet, startRow: currentRow, copy })

    return worksheet
}

export async function exportMissingAssetDataToExcel({
    assetsWithValuations,
    copy,
    locale = 'de-DE',
}: {
    assetsWithValuations: AssetWithValuations[]
    copy: MissingDataExportCopy
    locale?: string
}) {
    const workbook = new ExcelJS.Workbook()

    for (const assetWithValuations of assetsWithValuations) {
        transformSingleAssetMissingAssetDataToWorksheet({
            workbook,
            assetWithValuations,
            copy,
        })
    }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

    FileSaver.saveAs(blob, `${copy.fileNamePrefix}-${dayjs().format(getFileNameDateFormat(locale))}.xlsx`)
}
